import {Button, Form, Input, Select} from 'antd';
import {Formik} from 'formik';
import React, {useState} from 'react';
import * as Yup from 'yup';

import {ClientData, QuestionnaireRequestParams} from '../../../../types/src';
import {useErrorHandler} from '../../../../../utils/hooks';
import {FormRow, RequiredLabel} from '../formComponents';

import './registerForm.scss';

interface RegisterStep2Props {
  client: ClientData;
  onboardQuestionnaire: (params: QuestionnaireRequestParams) => Promise<void>;
  onboardQuestionnaireSkip: () => Promise<void>;
}

const RegisterStep2Schema = Yup.object().shape({
  heard_from: Yup.string().required('Required').max(300, 'Allowed 300 characters max'),
  outbound_sales_reps: Yup.string().required('Required'),
});

const HEARD_FROM = [
  {id: 1, value: 'Facebook'},
  {id: 2, value: 'LinkedIn'},
  {id: 3, value: 'Email'},
  {id: 4, value: 'Referral'},
  {id: 5, value: 'Search Engine'},
  {id: 6, value: 'Other'},
];

const OUTBOUND_SALES_REPS = [
  {id: 1, value: '1'},
  {id: 2, value: '2 to 5'},
  {id: 3, value: '5 to 10'},
  {id: 4, value: '10+'},
];

const initialValues: QuestionnaireRequestParams = {
  heard_from: undefined,
  outbound_sales_reps: undefined,
};

export const RegisterStep2 = ({
  client: {loading},
  onboardQuestionnaire,
  onboardQuestionnaireSkip,
}: RegisterStep2Props) => {
  const [otherChosen, setOtherChosen] = useState(false);
  const {handleError, clearError} = useErrorHandler();

  const handleSubmit = async (values: QuestionnaireRequestParams) => {
    clearError();

    try {
      await onboardQuestionnaire(values);
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={RegisterStep2Schema} onSubmit={handleSubmit}>
      {({values, handleSubmit, setFieldValue, errors, touched}) => (
        <Form className="registerForm" onFinish={handleSubmit} layout="vertical">
          <FormRow>
            <Form.Item
              label={<RequiredLabel label="How did you hear about us?" />}
              colon={false}
              validateStatus={errors.heard_from && touched.heard_from ? 'error' : ''}
              hasFeedback
              help={errors.heard_from && touched.heard_from ? errors.heard_from : ''}
            >
              <Select
                placeholder="Select"
                value={values.heard_from}
                onChange={(val: string) => {
                  setFieldValue('heard_from', val);
                  setOtherChosen(val === 'Other');
                }}
              >
                {HEARD_FROM.map(hearItem => (
                  <Select.Option key={hearItem.id} value={hearItem.value}>
                    {hearItem.value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </FormRow>
          {otherChosen && (
            <FormRow>
              <Form.Item
                label="Other Details"
                validateStatus={errors.heard_from && touched.heard_from ? 'error' : ''}
                hasFeedback
                help={errors.heard_from && touched.heard_from ? errors.heard_from : ''}
              >
                <Input.TextArea
                  placeholder="Input your option here"
                  autoSize={{minRows: 1, maxRows: 10}}
                  value={values.heard_from}
                  onChange={e => setFieldValue('heard_from', e.target.value)}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: errors.heard_from ? 'red' : undefined,
                  }}
                >
                  <span>{errors.heard_from ? 'Allowed 300 characters max ' : ''}</span>
                  <span>{(values.heard_from || '').length}/300</span>
                </div>
              </Form.Item>
            </FormRow>
          )}
          <FormRow>
            <Form.Item
              label={<RequiredLabel label="How many outbound sales reps are in your company?" />}
              colon={false}
              validateStatus={errors.outbound_sales_reps && touched.outbound_sales_reps ? 'error' : ''}
              hasFeedback
              help={errors.outbound_sales_reps && touched.outbound_sales_reps ? errors.outbound_sales_reps : ''}
            >
              <Select
                placeholder="Select"
                value={values.outbound_sales_reps}
                onChange={(val: string) => setFieldValue('outbound_sales_reps', val)}
              >
                {OUTBOUND_SALES_REPS.map(amountItem => (
                  <Select.Option key={amountItem.id} value={amountItem.value}>
                    {amountItem.value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </FormRow>
          <FormRow>
            <Form.Item>
              <Button type="primary" htmlType="submit" block loading={loading}>
                Submit
              </Button>
              <Button style={{marginTop: '10px'}} type="default" block onClick={onboardQuestionnaireSkip}>
                Skip
              </Button>
            </Form.Item>
          </FormRow>
        </Form>
      )}
    </Formik>
  );
};
